import { Injectable } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { DEVICE_TYPE } from '@finder/shared/services/viewport-detection/device-type.constant';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { Viewport } from './viewport-detection.interface';
import { SsrService } from '../ssr/ssr.service';

export const BREAKPOINTS = {
    desktopLarge: 1920,
    desktopMedium: 1440,
    desktop: 1024,
    tablet: 768,
};

/**
 * A service to determine the device type depending on the viewport.
 *
 * @class ViewportDetection
 */
@Injectable()
export class ViewportDetectionService {

    private viewport: DEVICE_TYPE;
    private resizing: Subject<DEVICE_TYPE>;
    private browserResizing: Subject<DEVICE_TYPE>;

    /**
     * @constructor
     */
    constructor(
        private winRef: WindowRef,
        private ssrService: SsrService,
    ) {
        this.viewport = this.getDevice();
        this.resizing = new Subject();
        this.browserResizing = new Subject();

        // Stop on SSR
        if (this.ssrService.isSSR()) {
            return;
        }

        // Debounce the resizing event since its fired at a high rate.
        const eventSource = fromEvent(winRef.nativeWindow, 'resize').pipe(debounceTime(150));
        // Subscribe to update the viewport.
        eventSource.subscribe((event) => {
            // For anyone listening let the know the browser resized
            this.browserResizing.next(this.viewport);

            // This is when the browser changes device sizes
            const updatedViewport = this.getDevice();
            if (this.viewport !== updatedViewport) {
                this.viewport = updatedViewport;
                this.resizing.next(this.viewport);
            }
        });
    }

    /**
     * Determines the device type depending on the current viewport.
     *
     * @returns
     */
    public getDevice(): DEVICE_TYPE {
        const width = this.winRef.nativeWindow.innerWidth;
        let viewport = DEVICE_TYPE.mobile;

        if (width >= BREAKPOINTS.desktop) {
            viewport = DEVICE_TYPE.desktop;
        } else if (width >= BREAKPOINTS.tablet) {
            viewport = DEVICE_TYPE.tablet;
        }

        return viewport;
    }

    /**
     * Calculates whether current viewport belongs to a mobile device or not.
     *
     * @returns
     */
    isMobile(): boolean {
        return this.getDevice() === DEVICE_TYPE.mobile;
    }

    /**
     * Calculates whether current viewport belongs to a tablet device or not.
     *
     * @returns
     */
    isTablet(): boolean {
        return this.getDevice() === DEVICE_TYPE.tablet;
    }

    /**
     * Calculates whether current viewport belongs to a desktop device or not.
     *
     * @returns
     */
    isDesktop(): boolean {
        return this.getDevice() === DEVICE_TYPE.desktop;
    }

    /**
     * Returns an observable which can be subscribed to listen for device detection changes.
     *
     * @returns
     */
    onViewportResize(): Subject<DEVICE_TYPE> {
        return this.resizing;
    }

    /**
     * Returns the dimensions of the browser on a resize event
     *
     * @returns
     */
    onBrowserResize(): Subject<DEVICE_TYPE> {
        return this.browserResizing;
    }

    /**
     * Determines the breakpoint name depending on the current viewport.
     *
     * @returns
     */
    getViewportBreakpoint(): string {
        const width = this.winRef.nativeWindow.innerWidth;
        let viewportSize: string;

        if (width >= BREAKPOINTS.desktopLarge) {
            viewportSize = 'desktop-large';
        } else if (width >= BREAKPOINTS.desktopMedium) {
            viewportSize = 'desktop-medium';
        } else if (width >= BREAKPOINTS.desktop) {
            viewportSize = 'desktop';
        } else if (width >= BREAKPOINTS.tablet) {
            viewportSize = 'tablet';
        } else {
            viewportSize = 'mobile';
        }

        return viewportSize;
    }

    getViewportSize(): Viewport {
        return {
            width: this.winRef.nativeWindow.innerWidth,
            height: this.winRef.nativeWindow.innerHeight
        };
    }
}
